import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scale, Clock, CheckCircle, XCircle, AlertTriangle, Eye, Search, Filter, RefreshCw, Ticket, CreditCard, Mail, Phone, Calendar, FileText, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';

export default function WithdrawalAdminPage() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [adminNotes, setAdminNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [requestsRes, statsRes] = await Promise.all([
        api.get(`/withdrawal/admin/requests${filter !== 'all' ? `?status=${filter}` : ''}`),
        api.get('/withdrawal/admin/stats')
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedRequest) return;
    
    if (actionType === 'reject' && adminNotes.length < 10) {
      toast.error('El motivo de rechazo debe tener al menos 10 caracteres');
      return;
    }

    setIsProcessing(true);
    try {
      if (actionType === 'approve') {
        await api.post(`/withdrawal/admin/requests/${selectedRequest.id}/approve${adminNotes ? `?admin_notes=${encodeURIComponent(adminNotes)}` : ''}`);
        toast.success('Solicitud aprobada');
      } else {
        await api.post(`/withdrawal/admin/requests/${selectedRequest.id}/reject?rejection_reason=${encodeURIComponent(adminNotes)}`);
        toast.success('Solicitud rechazada');
      }
      setShowModal(false);
      setSelectedRequest(null);
      setAdminNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setIsProcessing(false);
    }
  };

  const openModal = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setAdminNotes('');
    setShowModal(true);
  };

  const filteredRequests = requests.filter(r => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      r.full_name.toLowerCase().includes(searchLower) ||
      r.email.toLowerCase().includes(searchLower) ||
      r.dni.includes(search) ||
      r.id.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium flex items-center gap-1"><Clock size={12} /> Pendiente</span>;
      case 'approved':
        return <span className="px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium flex items-center gap-1"><CheckCircle size={12} /> Aprobada</span>;
      case 'rejected':
        return <span className="px-2 py-1 rounded-full bg-red-500/20 text-red-400 text-xs font-medium flex items-center gap-1"><XCircle size={12} /> Rechazada</span>;
      default:
        return null;
    }
  };

  const getTypeIcon = (type) => {
    return type === 'ticket' ? <Ticket size={16} className="text-purple-400" /> : <CreditCard size={16} className="text-blue-400" />;
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-xl flex items-center justify-center">
              <Scale className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Solicitudes de Arrepentimiento</h1>
              <p className="text-white/50 text-sm">Ley 24.240 - Defensa del Consumidor</p>
            </div>
          </div>
          <Button onClick={fetchData} variant="outline" className="border-white/10 text-white/70 hover:text-white">
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#111] rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-center justify-between">
              <span className="text-white/50 text-sm">Pendientes</span>
              <AlertTriangle size={18} className="text-amber-400" />
            </div>
            <p className="text-2xl font-bold text-amber-400 mt-2">{stats.pending}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-[#111] rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-center justify-between">
              <span className="text-white/50 text-sm">Aprobadas</span>
              <CheckCircle size={18} className="text-emerald-400" />
            </div>
            <p className="text-2xl font-bold text-emerald-400 mt-2">{stats.approved}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#111] rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-center justify-between">
              <span className="text-white/50 text-sm">Rechazadas</span>
              <XCircle size={18} className="text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-400 mt-2">{stats.rejected}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-[#111] rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-center justify-between">
              <span className="text-white/50 text-sm">Total</span>
              <Scale size={18} className="text-[#D4AF37]" />
            </div>
            <p className="text-2xl font-bold text-white mt-2">{stats.total}</p>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email, DNI o ID..."
              className="pl-10 bg-[#111] border-white/10 text-white placeholder:text-white/30"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected'].map((f) => (
              <Button
                key={f}
                onClick={() => setFilter(f)}
                variant={filter === f ? 'default' : 'outline'}
                className={filter === f 
                  ? 'bg-[#D4AF37] text-black hover:bg-[#C4A030]' 
                  : 'border-white/10 text-white/70 hover:text-white'
                }
                size="sm"
              >
                {f === 'all' ? 'Todas' : f === 'pending' ? 'Pendientes' : f === 'approved' ? 'Aprobadas' : 'Rechazadas'}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#111] rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-white/50 font-medium text-sm">ID</th>
                  <th className="text-left p-4 text-white/50 font-medium text-sm">Tipo</th>
                  <th className="text-left p-4 text-white/50 font-medium text-sm">Solicitante</th>
                  <th className="text-left p-4 text-white/50 font-medium text-sm">Fecha Compra</th>
                  <th className="text-left p-4 text-white/50 font-medium text-sm">Estado</th>
                  <th className="text-left p-4 text-white/50 font-medium text-sm">Fecha Solicitud</th>
                  <th className="text-right p-4 text-white/50 font-medium text-sm">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-white/50">
                      <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                      Cargando...
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-white/50">
                      No hay solicitudes para mostrar
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr key={request.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="p-4">
                        <span className="font-mono text-[#D4AF37] font-medium">{request.id}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(request.withdrawal_type)}
                          <span className="text-white/70 text-sm capitalize">
                            {request.withdrawal_type === 'ticket' ? 'Entrada' : 'Suscripción'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{request.full_name}</p>
                          <p className="text-white/50 text-sm">{request.email}</p>
                        </div>
                      </td>
                      <td className="p-4 text-white/70">
                        {new Date(request.purchase_date).toLocaleDateString('es-AR')}
                      </td>
                      <td className="p-4">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="p-4 text-white/50 text-sm">
                        {new Date(request.created_at).toLocaleDateString('es-AR')}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedRequest(request); setShowModal(true); setActionType(null); }}
                            className="text-white/50 hover:text-white"
                          >
                            <Eye size={16} />
                          </Button>
                          {request.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => openModal(request, 'approve')}
                                className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Aprobar
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => openModal(request, 'reject')}
                                className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                              >
                                <XCircle size={14} className="mr-1" />
                                Rechazar
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        <AnimatePresence>
          {showModal && selectedRequest && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#111] rounded-2xl border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white">Solicitud #{selectedRequest.id}</h2>
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {/* Type */}
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    {getTypeIcon(selectedRequest.withdrawal_type)}
                    <span className="text-white capitalize">
                      {selectedRequest.withdrawal_type === 'ticket' ? 'Entrada / Ticket' : 'Suscripción'}
                    </span>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
                        <User size={14} />
                        Nombre
                      </div>
                      <p className="text-white">{selectedRequest.full_name}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
                        <FileText size={14} />
                        DNI
                      </div>
                      <p className="text-white">{selectedRequest.dni}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
                        <Mail size={14} />
                        Email
                      </div>
                      <p className="text-white text-sm break-all">{selectedRequest.email}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
                        <Phone size={14} />
                        Teléfono
                      </div>
                      <p className="text-white">{selectedRequest.phone}</p>
                    </div>
                  </div>

                  {/* Purchase Info */}
                  <div className="pt-4 border-t border-white/10">
                    <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
                      <Calendar size={14} />
                      Fecha de compra
                    </div>
                    <p className="text-white">{new Date(selectedRequest.purchase_date).toLocaleDateString('es-AR')}</p>
                  </div>

                  {selectedRequest.order_number && (
                    <div>
                      <p className="text-white/50 text-sm mb-1">Número de orden</p>
                      <p className="text-[#D4AF37] font-mono">{selectedRequest.order_number}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-white/50 text-sm mb-1">Descripción de la compra</p>
                    <p className="text-white/80 bg-white/5 rounded-lg p-3 text-sm">{selectedRequest.purchase_description}</p>
                  </div>

                  {selectedRequest.reason && (
                    <div>
                      <p className="text-white/50 text-sm mb-1">Motivo</p>
                      <p className="text-white/80 bg-white/5 rounded-lg p-3 text-sm">{selectedRequest.reason}</p>
                    </div>
                  )}

                  {/* Resolution Info */}
                  {selectedRequest.status !== 'pending' && (
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-white/50 text-sm mb-1">Resuelta por</p>
                      <p className="text-white">{selectedRequest.resolved_by} - {new Date(selectedRequest.resolved_at).toLocaleDateString('es-AR')}</p>
                      {selectedRequest.admin_notes && (
                        <div className="mt-2">
                          <p className="text-white/50 text-sm mb-1">Notas del admin</p>
                          <p className="text-white/80 bg-white/5 rounded-lg p-3 text-sm">{selectedRequest.admin_notes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Form */}
                  {actionType && selectedRequest.status === 'pending' && (
                    <div className="pt-4 border-t border-white/10">
                      <div className="mb-4">
                        <p className="text-white font-medium mb-2">
                          {actionType === 'approve' ? 'Aprobar Solicitud' : 'Rechazar Solicitud'}
                        </p>
                        <Textarea
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          placeholder={actionType === 'approve' ? 'Notas adicionales (opcional)' : 'Motivo del rechazo (obligatorio)'}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                          rows={3}
                        />
                        {actionType === 'reject' && (
                          <p className="text-white/40 text-xs mt-1">Mínimo 10 caracteres</p>
                        )}
                      </div>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => setActionType(null)}
                          variant="outline"
                          className="flex-1 border-white/10 text-white/70"
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleAction}
                          disabled={isProcessing || (actionType === 'reject' && adminNotes.length < 10)}
                          className={`flex-1 ${
                            actionType === 'approve' 
                              ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                              : 'bg-red-500 hover:bg-red-600 text-white'
                          }`}
                        >
                          {isProcessing ? 'Procesando...' : actionType === 'approve' ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                {!actionType && (
                  <div className="p-6 border-t border-white/10">
                    <Button
                      onClick={() => setShowModal(false)}
                      className="w-full bg-white/10 text-white hover:bg-white/20"
                    >
                      Cerrar
                    </Button>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
}
